import { Component, inject, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { DataSets, LineComponent } from '../line-chart/line.component';
import { AuthService } from '../../services/auth/auth.service';
import { CustomerService } from '../../services/customer/customer.service';
import { HelperService } from '../../services/helper/helper.service';
import { ChartService } from '../../services/charts/chart.service';
import { HomeService } from '@services/home/home.service';
import { OnboardingState, Details, Persons } from '@airwallet/shared-models/details';
import { DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { Subject } from 'rxjs';
import { RouterLink } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { OperatorService } from '../../services/operator/operator.service';
import { ActionsService } from '../../services/actions/actions.service';
import { Action } from '@airwallet/shared-models/operator/actions';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { SortService } from '../../services/sorting/sort.service';
import { ShallowUserRecord } from '@airwallet/shared-models/auth-user-record';
import { OnboardingService } from '../../services/onboarding/onboarding.service';
import { ContractExpiringListComponent } from './contract-expiring-list/contract-expiring-list.component';
import { ContractPerformanceListComponent } from './contract-performance-list/contract-performance-list.component';
import { SmallOverviewActionsCardComponent } from '@components/actions/small-overview-actions-card/small-overview-actions-card.component';
import { CustomToolTipComponent } from '../misc/custom-tool-tip/custom-tool-tip.component';
import { FormsModule } from '@angular/forms';
import { OnboardingComponent } from './onboarding/onboarding.component';
import { LoadingComponent } from '../loading/loading.component';
import { NgIf, NgFor } from '@angular/common';
import { PayoutSettings, SpecificManualPayoutData } from '@airwallet/shared-models/payouts';
import { ActiveUsers } from '@airwallet/shared-models/customerStats';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { MixpanelService } from '@services/mixpanel/mixpanel.service';
import dayjs from 'dayjs';
import moment from 'moment';
import { BusinessType } from '@airwallet/shared-models/stripe';
import { FeatureService } from '@services/feature-toggle/feature.service';
import { Auth } from '@angular/fire/auth';
import { ToastService } from '@services/helper/toast.service';
import { SidebarService } from '@services/sidebar/sidebar.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InstantPayoutModalComponent } from './instant-payout-modal/instant-payout-modal.component';
import { NewFeaturesModalComponent } from '@components/home/new-features-modal/new-features-modal.component';
import { environment } from 'src/environments/environment';
import { IntroductionModalComponent } from './introduction-modal/introduction-modal.component';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrl: './home.component.scss',
    imports: [
        NgIf,
        LoadingComponent,
        OnboardingComponent,
        FormsModule,
        CustomToolTipComponent,
        LineComponent,
        RouterLink,
        NgFor,
        SmallOverviewActionsCardComponent,
        LottieComponent,
        ContractPerformanceListComponent,
        ContractExpiringListComponent,
        TranslateModule,
        InstantPayoutModalComponent
        // AwButtonComponent // instant payout button is not in use right now
    ]
})
export class HomeComponent implements OnInit, OnDestroy {
    @ViewChild(LineComponent, { static: false }) line: LineComponent;
    @ViewChild(NewFeaturesModalComponent, { static: false }) newFeaturesModalComponent: NewFeaturesModalComponent;
    loadingCustomerDetails: Signal<boolean> = this.homeService.loadingCustomerDetails;
    customerDetails: Signal<Details> = this.homeService.customerDetails;
    onboardingState: Signal<OnboardingState> = this.onboardingService.onboardingState;

    showLoadingIndicatorForPayoutDetails = true;
    viewOptionForCharts: 'week' | 'month' | 'year' = 'month';
    activeUsersData: ActiveUsers;
    active_users_present = 0;
    diffInActiveUsersFromPastToPresent = 0;
    totalRevenue: string = this.helperService.localizeNumberWithCurrency(0);
    nextPayoutDay: string;
    sumPayout: string;
    specificManualPayoutData: SpecificManualPayoutData[] = [];
    autoPayout: boolean;
    dataSets: DataSets;
    showSmallLoadingIndicator = false;
    persons: Persons = {
        representative: {
            first_name: '',
            last_name: '',
            title: ''
        },
        verified: false
    };
    user: DashboardOwnerUser = this.helperService.getDashboardOwnerUser();
    actions: Action[] = [];
    isSkeleton = true;
    actionLoading = false;
    fromDate: number;
    toDate: number;
    payoutSettings: PayoutSettings;

    options: AnimationOptions = {
        // https://www.npmjs.com/package/ngx-lottie
        path: '../../../assets/animations/jumping_star.json'
    };
    controlledUserActive: boolean;
    onboardingComplete: Signal<boolean> = this.onboardingService.onboardingComplete;
    companyName: string;
    payoutAmount: number;
    currency: string;
    isDemo = environment.isDemo;
    isActionsEnabled: Signal<boolean> = this.featureService.getFeatureAccess('actions', 'route_access');
    private ngUnsubscribe = new Subject<void>();

    private auth: Auth = inject(Auth);

    constructor(
        protected authService: AuthService,
        private customerService: CustomerService,
        private helperService: HelperService,
        private chartService: ChartService,
        private toastService: ToastService,
        private homeService: HomeService,
        private translate: TranslateService,
        public actionService: ActionsService,
        private mixpanelService: MixpanelService,
        private operatorService: OperatorService,
        private onboardingService: OnboardingService,
        private localStorageService: LocalStorageService,
        protected featureService: FeatureService,
        protected sidebarService: SidebarService,
        protected modalService: NgbModal
    ) {
        dayjs().locale(this.translate.currentLang);
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    async ngOnInit() {
        this.fromDate = dayjs().subtract(29, 'days').startOf('day').unix() + dayjs().utcOffset() * 60;
        this.controlledUserActive = this.helperService.isUserControlled();
        this.setSubscriptions();
        this.getActions();

        if (this.isDemo && !this.localStorageService.getItem('introductionModalDismissed')) {
            await this.openIntroductionModal();
        } else if (!this.localStorageService.getItem('newFeaturesModalDismissed') && !this.isDemo) {
            // For new feature releases we can use this modal to inform about the changes released.
            // await this.openNewFeaturesModal();
        }
    }

    openIntroductionModal() {
        return new Promise<void>((resolve, reject) => {
            let modalRef = this.modalService.open(IntroductionModalComponent, {
                size: 'lg',
                keyboard: false // Prevent dismissal by pressing the Escape key
            });

            modalRef.result.then(
                result => {
                    if (result === 'success') {
                        console.log('Dismissed introduction modal');
                        this.removeIntroductionModal();
                        resolve();
                    } else {
                        this.removeIntroductionModal();
                    }
                    modalRef = null; // Reset the modal reference on close
                },
                reason => {
                    modalRef = null; // Reset the modal reference on dismiss
                    this.removeIntroductionModal();
                    reject();
                }
            );
        });
    }

    openNewFeaturesModal() {
        return new Promise<void>((resolve, reject) => {
            let modalRef = this.modalService.open(NewFeaturesModalComponent, {
                keyboard: false // Prevent dismissal by pressing the Escape key
            });
            modalRef.result.then(
                result => {
                    if (result === 'success') {
                        console.log('Dismissed new features modal');
                        this.removeNewFeaturesModal();
                        resolve();
                    } else {
                        this.removeNewFeaturesModal();
                    }
                    modalRef = null; // Reset the modal reference on close
                },
                reason => {
                    modalRef = null; // Reset the modal reference on dismiss
                    this.removeNewFeaturesModal();
                    reject();
                }
            );
        });
    }

    removeNewFeaturesModal() {
        this.localStorageService.setItem('newFeaturesModalDismissed', true);
    }

    removeIntroductionModal() {
        this.localStorageService.setItem('introductionModalDismissed', true);
    }

    async setSubscriptions() {
        await this.homeService.getCustomerDetails(this.user.uid).then(async () => {
            await this.onboardingService.getOnboardingStatus();
        });
        this.handleVerification();

        if (this.customerDetails().business_type === BusinessType.COMPANY) {
            this.companyName = await this.customerService.readCompanyName(this.user.uid);
        }

        if (this.onboardingComplete() === true) {
            const persons = this.customerDetails()?.persons;
            if (persons) {
                for (const key in persons) {
                    if (persons[key]?.relationship?.representative) {
                        this.persons = {
                            representative: {
                                first_name: persons[key]?.first_name,
                                last_name: persons[key]?.last_name,
                                title: persons[key]?.relationship?.title
                            },
                            verified: true
                        };
                    }
                    // if requirements is found on any of the persons attached to the account, the verified state should be false.
                    this.persons.verified = persons[key].requirements ? false : this.persons.verified;
                }
            }

            this.active_users_present = 0;
            this.diffInActiveUsersFromPastToPresent = 0;
            this.getNextPayoutAmount();
            // backend_translates
            const translates = {
                current_lang: this.translate.currentLang,
                overview_today: this.translate.instant('overview.today')
            };

            this.chartService.getChartData(translates).then(orderStats => {
                this.dataSets = orderStats;
                if (this.line) {
                    this.line.setChart(this.dataSets ? this.dataSets.last30Days.line : []);
                    this.viewOptionForCharts = 'month';
                }
            });

            this.getActiveUserData();
        }
    }

    goToStripeKYC() {
        if ((this.onboardingState() as any).is_andorra) {
            this.toastService.info(this.translate.instant('overview.is_andorra'));
            return;
        }
        this.showSmallLoadingIndicator = true;
        this.onboardingService.getStripeKYCUrl().catch(error => {
            console.error(error);
            this.showSmallLoadingIndicator = false;
        });
    }

    async handleVerification() {
        // verify_email is a value in UNIX UTC seconds set on account creation.
        if (this.customerDetails() && this.customerDetails().verify_email) {
            if (this.controlledUserActive) {
                await this.homeService
                    .fetchUserAsAdmin()
                    .then(async data => {
                        const userRecord: ShallowUserRecord = data;
                        if (!userRecord.email_verified && dayjs().unix() > dayjs.unix(this.customerDetails().verify_email).add(24, 'hour').unix()) {
                            if (userRecord.email) {
                                this.helperService.verifyEmailToast(userRecord.email);
                            } else {
                                this.toastService.error(this.translate.instant('overview.no_email_found'), null, { disableTimeOut: true });
                            }
                        } else {
                            this.customerService.removeVerifyEmailTimestamp(this.user.uid); // fire and forget
                        }
                    })
                    .catch(err => {
                        console.error(err);
                    });
            } else if (!this.auth.currentUser.emailVerified) {
                // If emailed not verified, and it is more than 24h since the account was created
                if (dayjs().unix() > dayjs.unix(this.customerDetails().verify_email).add(24, 'hour').unix()) {
                    this.helperService.verifyEmailToast(this.auth.currentUser.email);
                }
            } else {
                this.customerService.removeVerifyEmailTimestamp(this.user.uid); // fire and forget
            }
        }
    }

    handleDateChange(value: string) {
        switch (value) {
            case 'week':
                this.week();
                this.mixpanelService.track('RoT 7 Days Filter', { filter_value: value });
                break;
            case 'month':
                this.month();
                this.mixpanelService.track('RoT 1 Month Filter', { filter_value: value });
                break;
            case 'year':
                this.year();
                this.mixpanelService.track('RoT 1 Year Filter', { filter_value: value });
                break;
        }
    }

    week() {
        this.setFromDateForChild(7);
        if (this.dataSets && this.line) {
            this.line.setChart(this.dataSets ? this.dataSets.last7Days.line : []);
        }
        this.viewOptionForCharts = 'week';
        this.setActiveUser();
        this.UpdateRevenueOverPeriod();
    }

    month() {
        this.setFromDateForChild(30);
        if (this.dataSets && this.line) {
            this.line.setChart(this.dataSets ? this.dataSets.last30Days.line : []);
        }
        this.viewOptionForCharts = 'month';
        this.setActiveUser();
        this.UpdateRevenueOverPeriod();
    }

    year() {
        this.setFromDateForChild(365);
        if (this.dataSets && this.line) {
            this.line.setChart(this.dataSets ? this.dataSets.last12Months.line : []);
        }
        this.viewOptionForCharts = 'year';
        this.setActiveUser();
        this.UpdateRevenueOverPeriod();
    }

    getActiveUserData() {
        this.chartService.getUserStats().then(users_stats => {
            this.activeUsersData = users_stats;
            this.setActiveUser();
        });
    }

    setActiveUser() {
        if (this.activeUsersData) {
            this.active_users_present = this.activeUsersData[this.viewOptionForCharts].active_users_present;
            this.diffInActiveUsersFromPastToPresent = this.activeUsersData[this.viewOptionForCharts].diff_in_active_users_from_past_to_present;
        }
    }

    setFromDateForChild(days) {
        this.fromDate = dayjs().subtract(days, 'days').startOf('day').unix() + dayjs().utcOffset() * 60;
        this.operatorService.setFrom(this.fromDate);
    }

    async getNextPayoutAmount() {
        this.homeService.getNextPayoutAmount().then(payoutData => {
            console.log('Payout data received:', payoutData);
            this.payoutAmount = payoutData.amount ?? 0;
            this.currency = payoutData.currency.toUpperCase() ?? '';
            this.sumPayout = this.helperService.localizeNumberWithCurrency(payoutData.amount ? payoutData.amount / 100 : 0);
            this.nextPayoutDay = moment.unix(payoutData.arrival_date).format('Do MMM');
            this.autoPayout = payoutData.auto;
            for (const key in payoutData.specific_manual_payout_data) {
                this.specificManualPayoutData.push(payoutData.specific_manual_payout_data[key]);
            }
            this.showLoadingIndicatorForPayoutDetails = false;
            this.showSmallLoadingIndicator = false;
        });
    }

    getPayoutDateAmountString(payout: SpecificManualPayoutData): string {
        return `${moment.unix(payout.arrival_date).format('Do MMM')} - ${this.helperService.localizeNumberWithCurrency(payout.amount / 100, 2, 2, payout.curreny)}`;
    }

    async getActions() {
        if (!this.isActionsEnabled()) return;
        this.actionLoading = true;
        this.actions = await this.actionService.getActionsFromBackend().then(response => {
            this.actionLoading = false;
            const actions: Action[] = this.actionService.convertActionsToArray(response);
            this.isSkeleton = false;
            actions.sort(SortService.byPropertiesOf(['log'], 'timestamp'));
            return this.filterActionsToOnlyThree(actions);
        });
    }

    showNoActionsBox(): boolean {
        return this.actionService.showNoActionsBox(this.actions, false);
    }

    filterActionsToOnlyThree(actions: Action[]): Action[] {
        let index = 0;
        const filteredActions: Action[] = [];
        for (const action of actions) {
            if (action.active_actions) {
                if (index < 3) {
                    index++;
                    action.item_number = index;
                    filteredActions.push(action);
                }
            }
        }
        return filteredActions;
    }

    UpdateRevenueOverPeriod() {
        if (!this.dataSets) return;

        switch (this.viewOptionForCharts) {
            case 'week': {
                let sum = 0;
                this.dataSets.last7Days.line.forEach(elem => {
                    sum += !isNaN(elem.amount) ? elem.amount : 0;
                });
                sum = sum / 100;
                this.totalRevenue = this.helperService.localizeNumberWithCurrency(sum ? sum : 0);
                break;
            }
            case 'month': {
                let sum = 0;
                this.dataSets.last30Days.line.forEach(elem => {
                    sum += !isNaN(elem.amount) ? elem.amount : 0;
                });
                sum = sum / 100;
                this.totalRevenue = this.helperService.localizeNumberWithCurrency(sum ? sum : 0);
                break;
            }
            case 'year': {
                let sum = 0;
                this.dataSets.last12Months.line.forEach(elem => {
                    sum += !isNaN(elem.amount) ? elem.amount : 0;
                });
                sum = sum / 100;
                this.totalRevenue = this.helperService.localizeNumberWithCurrency(sum ? sum : 0);
                break;
            }
        }
    }

    onLinkClick(route: string) {
        if (route === 'actions') {
            this.mixpanelService.track('Action: Go to Actions List', { action: 'go to actions list from home' });
        }
    }
}
